import React, { useMemo, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHistory, faPen, faLink, faCirclePlus, faChartLine } from "@fortawesome/free-solid-svg-icons";
import "./TablaCRMSeguimiento.css";

const formatearFecha = (fecha) => {
    if (!fecha) return "--";
    const d = new Date(fecha);
    if (Number.isNaN(d.getTime())) return "--";
    return d.toLocaleString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const extraerNombre = (observacion) => {
    if (!observacion) return "";
    if (!(observacion.includes("[NUEVO]") || observacion.includes("Prospecto:"))) return "";

    try {
        const limpio = observacion.replace("[NUEVO]", "").replace("Prospecto:", "").trim();
        const partes = limpio.split("|");
        return partes[0]?.trim() || "";
    } catch {
        return "";
    }
};

const TablaCRMSeguimiento = ({
    datos = [],
    loading = false,
    darkMode = false,
    onVerHistorial,
    onAgregarSeguimiento,
    onEditarSeguimiento,
    onAsignarAlta,
    onVerFacturacion,
}) => {
    const [busqueda, setBusqueda] = useState("");

    const datosFiltrados = useMemo(() => {
        const texto = busqueda.trim().toLowerCase();

        let lista = Array.isArray(datos) ? [...datos] : [];

        if (texto) {
            lista = lista.filter((x) => {
                const nombre = extraerNombre(x.observacion);
                const fuente = [
                    x.cuit || "",
                    x.telefono || "",
                    x.estadoResultante || "",
                    x.reglaAplicada?.aliasVista || "",
                    x.reglaAplicada?.ambito || "",
                    x.observacion || "",
                    nombre,
                    x.idAlta?.toString() || "",
                ]
                    .join(" ")
                    .toLowerCase();

                return fuente.includes(texto);
            });
        }

        return lista.sort((a, b) => new Date(b.fechaGestion) - new Date(a.fechaGestion));
    }, [datos, busqueda]);

    if (loading) {
        return (
            <div className="crm-seguimiento-loading">
                <Spinner animation="border" size="sm" className="me-2" />
                Cargando seguimiento de postventa...
            </div>
        );
    }

    return (
        <section className={`crm-seguimiento-wrap ${darkMode ? "is-dark" : ""}`}>
            <div className="crm-seguimiento-toolbar">
                <Form.Control
                    type="text"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Buscar por CUIT, telefono, estado, accion, observacion..."
                    className="crm-seguimiento-search"
                />
            </div>

            <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                    <thead className="table-dark">
                        <tr>
                            <th>Alerta</th>
                            <th>Accion</th>
                            <th>Estado</th>
                            <th>CUIT</th>
                            <th>Telefono</th>
                            <th>Alta ID</th>
                            <th>Gestion</th>
                            <th>Proximo contacto</th>
                            <th>Observacion</th>
                            <th className="text-center">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {datosFiltrados.length === 0 ? (
                            <tr>
                                <td colSpan={10} className="text-center py-4 text-muted">
                                    No hay registros para mostrar.
                                </td>
                            </tr>
                        ) : (
                            datosFiltrados.map((item) => (
                                <tr key={item.id}>
                                    <td>
                                        <span className={`crm-pill ${item.alertaActiva ? "ok" : "off"}`}>
                                            {item.alertaActiva ? "Activa" : "Cerrada"}
                                        </span>
                                    </td>
                                    <td>{item.reglaAplicada?.aliasVista || "-"}</td>
                                    <td>{item.estadoResultante || "-"}</td>
                                    <td>{item.cuit || ""}</td>
                                    <td>{item.telefono || ""}</td>
                                    <td>{item.idAlta ?? "-"}</td>
                                    <td>{formatearFecha(item.fechaGestion)}</td>
                                    <td>{formatearFecha(item.fechaProximoContacto)}</td>
                                    <td className="crm-seguimiento-observacion" title={item.observacion || ""}>
                                        {item.observacion || "-"}
                                    </td>
                                    <td>
                                        <div className="crm-actions-group">
                                            <button
                                                type="button"
                                                className="crm-action-btn is-history"
                                                title="Ver historial"
                                                onClick={() => onVerHistorial?.(item)}
                                                disabled={!onVerHistorial}
                                            >
                                                <FontAwesomeIcon icon={faHistory} />
                                            </button>
                                            <button
                                                type="button"
                                                className="crm-action-btn is-add"
                                                title="Agregar seguimiento"
                                                onClick={() => onAgregarSeguimiento?.(item)}
                                                disabled={!onAgregarSeguimiento}
                                            >
                                                <FontAwesomeIcon icon={faCirclePlus} />
                                            </button>
                                            <button
                                                type="button"
                                                className="crm-action-btn is-edit"
                                                title="Editar gestion"
                                                onClick={() => onEditarSeguimiento?.(item)}
                                                disabled={!onEditarSeguimiento}
                                            >
                                                <FontAwesomeIcon icon={faPen} />
                                            </button>
                                            <button
                                                type="button"
                                                className="crm-action-btn is-link"
                                                title="Asignar Alta ID"
                                                onClick={() => onAsignarAlta?.(item)}
                                                disabled={!onAsignarAlta}
                                            >
                                                <FontAwesomeIcon icon={faLink} />
                                            </button>
                                            <button
                                                type="button"
                                                className="crm-action-btn is-chart"
                                                title="Ver facturacion"
                                                onClick={() => onVerFacturacion?.(item)}
                                                disabled={!onVerFacturacion}
                                            >
                                                <FontAwesomeIcon icon={faChartLine} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

export default TablaCRMSeguimiento;
